import React, { useEffect, useState } from 'react';
import { FiEye, FiEyeOff, FiHelpCircle, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { format } from 'date-fns';
import { aiTokenService } from '../../../services/aiTokenService';

const STATUS_BADGES = {
  valid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Connected' },
  invalid: { bg: 'bg-red-100', text: 'text-red-800', label: 'Invalid credentials' },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Not tested yet' },
};

export default function MidjourneyCard({ isManager, alert, confirm }) {
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discordToken, setDiscordToken] = useState('');
  const [guildId, setGuildId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await aiTokenService.getConnections('midjourney');
    if (error) {
      setMessage({ type: 'error', text: `Couldn't load your saved connection: ${error.message}` });
    }
    setConnection(data?.[0] || null);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!discordToken.trim() || !guildId.trim() || !channelId.trim()) {
      setMessage({ type: 'error', text: 'Discord token, guild ID, and channel ID are all required.' });
      return;
    }
    setIsSaving(true);
    setMessage(null);
    const { error } = await aiTokenService.saveMidjourneyConnection({ discordToken, guildId, channelId });
    setIsSaving(false);
    if (error) {
      setMessage({ type: 'error', text: error.message });
      return;
    }
    setDiscordToken('');
    setMessage({ type: 'success', text: 'Image-generation connection saved successfully.' });
    await load();
  };

  const handleTest = async () => {
    setIsTesting(true);
    setMessage(null);
    const { data, error } = await aiTokenService.testMidjourneyConnection();
    setIsTesting(false);
    if (error) {
      setMessage({ type: 'error', text: error.message });
      return;
    }
    setMessage({ type: data.status === 'valid' ? 'success' : 'error', text: data.message });
    await load();
  };

  const handleDisconnect = async () => {
    const ok = await confirm('Are you sure you want to disconnect this provider? AI generation will stop until another provider is connected.');
    if (!ok) return;
    setIsSaving(true);
    const { error } = await aiTokenService.disconnectMidjourneyConnection();
    setIsSaving(false);
    if (error) {
      alert(error.message);
      return;
    }
    setConnection(null);
    setGuildId('');
    setChannelId('');
    setMessage(null);
    await load();
  };

  const badge = STATUS_BADGES[connection?.status] || STATUS_BADGES.pending;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">MidJourney Connection</h2>
        <p className="text-sm text-gray-500">Connect your own personal Discord automation to create images through MidJourney — generating in your own server, never a shared account.</p>
      </div>

      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
        MidJourney has no official public API — this connects your own personal Discord automation, the same way the platform's own MidJourney generation already works, just scoped entirely to your account. Never share your Discord account password; only the token you configure here is stored, and it's encrypted.
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500 text-sm">Loading…</div>
      ) : connection ? (
        <div className="border border-gray-200 rounded-xl p-5 mb-4">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="min-w-0">
              <h4 className="font-semibold text-gray-900 mb-1">Your Discord connection</h4>
              <code className="text-xs text-gray-700 block">Token: ••••••••{connection.last_four}</code>
              <code className="text-xs text-gray-500 block mt-1">Channel: {connection.discord_channel_id}</code>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${badge.bg} ${badge.text}`}>{badge.label}</span>
          </div>
          <p className="text-xs text-gray-400 mb-3">
            Last tested: {connection.last_tested_at ? format(new Date(connection.last_tested_at), 'yyyy-MM-dd HH:mm') : 'Never'}
          </p>
          <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
            <button
              onClick={handleTest}
              disabled={isTesting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {isTesting ? 'Testing…' : 'Test Connection'}
            </button>
            <button
              onClick={handleDisconnect}
              disabled={isSaving}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-purple-200 rounded-xl p-6 text-center mb-4">
          <p className="text-sm text-gray-500">
            {isManager
              ? 'No personal connection saved — your existing manager MidJourney setup keeps working as the fallback.'
              : 'No MidJourney connection yet. Add your own Discord token and channel below to enable image generation.'}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowHelp((v) => !v)}
        className="flex items-center gap-1.5 text-sm text-purple-600 hover:text-purple-700 font-medium mb-3"
      >
        <FiHelpCircle className="w-4 h-4" />
        Where do I find these?
        {showHelp ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
      </button>

      {showHelp && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 space-y-3">
          <div>
            <p className="font-medium text-gray-800 mb-1">Discord User Token</p>
            <p>
              Open Discord in your browser and log in → press F12 to open Developer Tools → go to the
              <strong> Network</strong> tab → click any request to <code className="bg-gray-200 px-1 rounded">discord.com/api</code> →
              in the request headers, copy the value of <strong>Authorization</strong>. That value is your token.
            </p>
          </div>
          <div>
            <p className="font-medium text-gray-800 mb-1">Server (Guild) ID</p>
            <p>
              In Discord, go to <strong>User Settings → Advanced</strong> and enable <strong>Developer Mode</strong>.
              Then right-click your server's icon in the left sidebar and choose <strong>Copy Server ID</strong>.
            </p>
          </div>
          <div>
            <p className="font-medium text-gray-800 mb-1">Channel ID</p>
            <p>
              With Developer Mode still enabled, right-click the specific channel where the MidJourney bot posts
              images and choose <strong>Copy Channel ID</strong>.
            </p>
          </div>
          <p className="text-xs text-gray-400 pt-1 border-t border-gray-200">
            Only enter credentials for your own Discord account. Never share this token with anyone else — it is encrypted before storage and never shown again after saving.
          </p>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Discord User Token</label>
          <div className="relative">
            <input
              type={showToken ? 'text' : 'password'}
              value={discordToken}
              onChange={(e) => setDiscordToken(e.target.value)}
              placeholder="Your personal Discord token"
              className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            />
            <button
              type="button"
              onClick={() => setShowToken(!showToken)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showToken ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Server (Guild) ID</label>
            <input
              type="text"
              value={guildId}
              onChange={(e) => setGuildId(e.target.value)}
              placeholder="Discord server ID"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Channel ID</label>
            <input
              type="text"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              placeholder="MidJourney channel ID"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : connection ? 'Update Connection' : 'Save Connection'}
        </button>
      </div>

      {message && (
        <p className={`text-sm mt-3 text-center ${message.type === 'error' ? 'text-red-600' : 'text-green-600'}`}>
          {message.text}
        </p>
      )}
    </div>
  );
}
